/*
 * commands.ts — Phase 14 command palette data model.
 *
 * A Command is the unit the palette renders + executes. `id` is the stable
 * key for the {#each} loop (no DOM thrash across re-filters); `title` is
 * the only thing the user reads + searches; `hint` is reserved for an
 * optional secondary label like "Recent" or "Theme" that future surfaces
 * may show beside the title; `shortcut` is the keybinding hint text shown
 * right-aligned. `run` is the callback the palette invokes on Enter or
 * click — App.svelte closes the palette before calling it so a command
 * that opens another modal (Share, Settings) doesn't fight focus.
 *
 * fuzzyFilter is intentionally simple: substring match on lowercase title,
 * with a tiny scoring ladder (exact > startsWith > includes > word-starts)
 * so the most predictable result lands at the top. V2 (per spec) may swap
 * in a real fuzzy library — for V1 the command list is small (~15 items)
 * and users tend to type the obvious prefix.
 */
export interface Command {
  id: string;
  title: string;
  hint?: string;
  shortcut?: string;
  run: () => void | Promise<void>;
}

export function fuzzyFilter(commands: Command[], query: string): Command[] {
  if (!query) return commands;
  const q = query.toLowerCase();
  return commands
    .map((c) => {
      const t = c.title.toLowerCase();
      const score =
        t === q
          ? 3
          : t.startsWith(q)
            ? 2
            : t.includes(q)
              ? 1
              : t.split(' ').some((w) => w.startsWith(q))
                ? 0.5
                : 0;
      return { c, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.c);
}
